import {
  fetchFutureCampaigns,
  getSubscription,
  fetchSubscriptionCampaigns,
  fetchSubscriptionSkippedWeeks,
  fetchSubscriptionWeek,
  getSubscriptionWeekMealSwaps,
  getWeekMenu,
  removeSubscriptionAddon,
  setSubscriptionAddon,
  updateSubscription,
  updateSubscriptionBillingAddress,
  updateSubscriptionShippingAddress,
  cancelSubscription as cancelPopsSubscription,
  reactivateSubscription as reactivatePopsSubscription,
  activateSubscriptionWeek,
  skipSubscriptionWeek,
  updateSubscriptionWeek,
  setSubscriptionWeekAddon,
  removeSubscriptionWeekAddon,
  updateSubscriptionWeekShippingAddress,
  removeSubscriptionMealSwap,
  createSubscriptionWeekMealSwap,
  optInToDiscountOption as optInToPopsDiscountOption,
  updateSubscriptionWeekAddons,
  updateSubscriptionAddons,
  fetchSubscriptionStartDates,
} from '@/clients/pops/client'
import { NEW_PROGRAM_WEEK_CAP } from '@/constants/program-dates'
import { Campaign } from '@/models/campaign'
import { CampaignWeek } from '@/models/campaign-week'
import type { Meal } from '@/models/meal'
import type { MealProgramSubscription } from '@/models/meal-program-subscription'
import type { MealProgramSubscriptionWeek } from '@/models/meal-program-subscription-week'
import MealSwap from '@/models/meal-swap'
import { SubscriptionWeekMenu } from '@/models/subscription-week-menu'
import { loadCampaign } from '@/stores/campaign/operations'
import { loadDeliveryZoneById } from '@/stores/delivery-zone/operations'
import {
  loadDiscountOption,
  loadDiscountOptionWeeks,
} from '@/stores/discount-option/operations'
import { $discountOptions } from '@/stores/discount-option/stores'
import type { Address, UpdateSubscriptionAddonsPayload } from '@/types/pops'
import dayjs from '@/utilities/dayjs-config'
import type { Dayjs } from '@/utilities/dayjs-config'
import { createSubscription } from '@/utilities/factories'
import { buildMealIdForSku, buildSku } from '@/utilities/sakara'

import {
  clearCampaignWeeks,
  clearMealSwaps,
  clearPromises,
  clearSkippedWeeks,
  clearSubscriptionWeeksMenus,
  setAllowedChangesForWeek,
  setCampaignWeek,
  setPromise,
  setSkippedWeeks,
  setSubscription,
  setWeekMealSwaps,
} from './actions'
import { $getAffectedWeeks } from './computed'
import {
  $promises,
  $skippedWeeks,
  $subscription,
  $subscriptionWeeks,
  $subscriptionWeeksMenus,
} from './stores'
import type { Addon } from '@/models/addon'

export const loadSubscription = async ({
  subscriptionId,
  force = false,
}: {
  subscriptionId: number
  force?: boolean
}) => {
  const promiseKey = `loadSubscription${subscriptionId}`

  if ($promises.get()[promiseKey] && !force) {
    return $promises.get()[promiseKey]
  }

  await reset()

  const promise = (async () => {
    if (!subscriptionId) return Promise.resolve()

    const rawSubscription = await getSubscription(subscriptionId)
    setSubscription({
      subscription: createSubscription(
        rawSubscription,
      ) as MealProgramSubscription,
    })

    const { ui_validations: uiValidations } = rawSubscription
    uiValidations?.forEach((uiValidation) => {
      const week = Object.keys(uiValidation)[0]
      const allowedChanges = uiValidation[week]
      setAllowedChangesForWeek({ week, allowedChanges })
    })

    const promises = [
      loadCampaignWeeks(),
      loadSkippedWeeks(),
      loadDeliveryZoneById({
        deliveryZoneId: $subscription.get()?.deliveryZoneId as number,
      }),
    ]

    return Promise.all(promises)
  })()

  setPromise({ promiseKey, promise })

  return promise
}

export const reloadSubscription = async () => {
  const weeksToReload = Object.values($subscriptionWeeks.get()).map(
    (subscriptionWeek) => subscriptionWeek.week,
  )

  await loadSubscription({
    subscriptionId: $subscription.get()?.id as number,
    force: true,
  })
  return loadSubscriptionWeeks({ weeks: weeksToReload, force: true })
}

export const loadSubscriptionWeek = async ({
  week,
  force = false,
}: {
  week: Dayjs
  force?: boolean
}) => {
  if (!$subscription.get()?.isSubscriptionWeek(week)) return Promise.resolve()

  const promiseKey = `loadSubscriptionWeek${week.format('YYYY-MM-DD')}`

  // If the week is already loading or loaded, don't load again unless force flag is set to true
  if ($promises.get()[promiseKey] && !force) {
    return $promises.get()[promiseKey]
  }

  const promise = (async () => {
    const rawSubscriptionWeek = await fetchSubscriptionWeek(
      $subscription.get()?.id as number,
      week.format('YYYY-MM-DD'),
    )

    if (rawSubscriptionWeek) {
      const subscriptionWeek = createSubscription(
        rawSubscriptionWeek,
      ) as MealProgramSubscriptionWeek

      await loadSubscriptionWeekMenu({ week, force })
      await loadDeliveryZoneById({
        deliveryZoneId: subscriptionWeek.deliveryZoneId,
      })

      $subscriptionWeeks.setKey(week.format('YYYY-MM-DD'), subscriptionWeek)
    }
  })()

  setPromise({ promiseKey, promise })

  return promise
}

export const loadSubscriptionWeeks = async ({
  weeks,
  force = false,
}: {
  weeks: Dayjs[]
  force?: boolean
}) => {
  return Promise.all(weeks.map((week) => loadSubscriptionWeek({ week, force })))
}

export const loadSubscriptionWeekMenu = async ({
  week,
  force = false,
}: {
  week: Dayjs
  force: boolean
}) => {
  if (!$subscription.get()?.isSubscriptionWeek(week)) return Promise.resolve()

  const promiseKey = `loadSubscriptionWeekMenu${week.format('YYYY-MM-DD')}`

  if ($promises.get()[promiseKey] && !force) {
    return $promises.get()[promiseKey]
  }

  const promise = (async () => {
    const rawSubscriptionWeekMenu = await getWeekMenu(
      $subscription.get()?.id as number,
      week,
    )

    $subscriptionWeeksMenus.setKey(
      week.format('YYYY-MM-DD'),
      new SubscriptionWeekMenu(week, rawSubscriptionWeekMenu),
    )
  })()

  setPromise({ promiseKey, promise })

  return promise
}

export const loadCampaignWeeks = async () => {
  const lastMonth = dayjs().startOf('month').subtract(1, 'month')
  const currentCampaigns = await fetchFutureCampaigns(
    lastMonth.format('YYYY-MM-DD'),
  )
  const subscriptionCampaigns = await fetchSubscriptionCampaigns(
    $subscription.get()?.id as number,
  )
  const rawCampaignWeeks = subscriptionCampaigns.filter((campaign) =>
    currentCampaigns.some((c) => c.id === campaign.campaign_id),
  )

  clearCampaignWeeks()

  await Promise.all(
    rawCampaignWeeks.map((rawCampaignWeek) =>
      loadCampaign({ campaignId: rawCampaignWeek.campaign_id }),
    ),
  )

  rawCampaignWeeks.forEach((rawCampaignWeek) =>
    setCampaignWeek({
      week: rawCampaignWeek.week,
      campaignWeek: new CampaignWeek(rawCampaignWeek),
    }),
  )
}

export const loadWeekMealSwaps = async ({
  week,
  force = false,
}: {
  week: Dayjs
  force?: boolean
}) => {
  const promiseKey = `loadWeekMealSwaps${week.format('YYYY-MM-DD')}`

  if ($promises.get()[promiseKey] && !force) {
    return $promises.get()[promiseKey]
  }

  const promise = (async () => {
    const rawMealSwaps = await getSubscriptionWeekMealSwaps(
      $subscription.get()?.id as number,
      week.format('YYYY-MM-DD'),
    )

    setWeekMealSwaps({
      week,
      mealSwaps: rawMealSwaps.map((rawMealSwap) => new MealSwap(rawMealSwap)),
    })
  })()

  setPromise({ promiseKey, promise })

  return promise
}

export const loadSkippedWeeks = async () => {
  const skippedWeeks = await fetchSubscriptionSkippedWeeks(
    $subscription.get()?.id as number,
  )
  setSkippedWeeks({ skippedWeeks })
}

/** Gets the closest week to the given week that is a part of the subscription. */
export const getNextSubscriptionWeek = async ({ week }: { week: Dayjs }) => {
  const nextSubscriptionWeek = $subscription
    .get()
    ?.getNextSubscriptionWeek(week) as Dayjs

  await loadSubscriptionWeek({ week: nextSubscriptionWeek })

  return $subscriptionWeeks.get()[nextSubscriptionWeek.format('YYYY-MM-DD')]
}

/**
 * Gets the closest week to the given week that is active
 * and is a part of the suscription.
 */
export const getNextActiveSubscriptionWeek = async ({
  week,
  force = false,
}: {
  week: Dayjs
  force?: boolean
}) => {
  const nextSubscriptionWeek = $subscription
    .get()
    ?.getNextSubscriptionWeek(week) as Dayjs
  const nextActiveWeek = dayjs(nextSubscriptionWeek)

  let isWeekSkipped = $skippedWeeks
    .get()
    .includes(nextActiveWeek.format('YYYY-MM-DD'))

  while (isWeekSkipped) {
    nextActiveWeek.add(1, 'weeks')
    isWeekSkipped = $skippedWeeks
      .get()
      .includes(nextActiveWeek.format('YYYY-MM-DD'))
  }

  await loadSubscriptionWeek({ week: nextActiveWeek, force })

  return $subscriptionWeeks.get()[nextActiveWeek.format('YYYY-MM-DD')]
}

/**
 * Gets the closest week to the given week that is active, is a part of the suscription,
 * and has at least one upcoming delivery.
 */
export const getNextDeliverySubscriptionWeek = async ({
  week,
}: {
  week: Dayjs
}) => {
  let subscriptionWeek = await getNextActiveSubscriptionWeek({ week })

  // If the next active subscription week has no deliveries, the proceeding active week will
  if (subscriptionWeek && !subscriptionWeek.hasUpcomingDeliveries) {
    subscriptionWeek = await getNextActiveSubscriptionWeek({
      week: dayjs(week).add(1, 'week'),
    })
  }

  return subscriptionWeek
}

/**
 * Gets the next week of the subscription that is active and hasn't been
 * billed yet.
 */
export const getNextActiveUnbilledSubscriptionWeek = async () => {
  const upcomingWeekToBeBilled = $subscription.get()?.upcomingWeekToBeBilled
  const subscriptionWeek = await getNextActiveSubscriptionWeek({
    week: upcomingWeekToBeBilled as Dayjs,
    force: true,
  })

  return subscriptionWeek
}

export const setSubscriptionName = async ({
  subscriptionName,
}: {
  subscriptionName: string
}) => {
  await updateSubscription($subscription.get()?.id as number, {
    name: subscriptionName,
  })
  await reloadSubscription()
}

export const setMeal = async ({
  meal,
  isAdding,
}: {
  week?: Dayjs
  meal: string
  isAdding: boolean
}) => {
  if (isAdding) {
    await addMeal({ meal })
  } else {
    await removeMeal({ meal })
  }
}

export const addMeal = async ({ meal }: { meal: string }) => {
  const meals = $subscription.get()?.meals
  if (meals?.includes(meal)) return

  meals?.push(meal) // TODO: check reactivity

  const sku = buildSku(
    $subscription.get()?.daysOfFood as number,
    meals as string[],
  )

  await setSku({ sku })
}

export const removeMeal = async ({ meal }: { meal: string }) => {
  const meals = $subscription.get()?.meals?.filter((m) => m !== meal)

  const sku = buildSku(
    $subscription.get()?.daysOfFood as number,
    meals as string[],
  )

  await setSku({ sku })
}

export const setSku = async ({ sku }: { sku: string }) => {
  await updateSubscription($subscription.get()?.id as number, { sku })
  await reloadSubscription()
}

export const setDaysOfFood = async ({ daysOfFood }: { daysOfFood: number }) => {
  const sku = buildSku(daysOfFood, $subscription.get()?.meals as string[])

  return await setSku({ sku })
}

export const setDeliveryWindow = async ({
  deliveryWindow,
}: {
  deliveryWindow: string[]
}) => {
  await updateSubscription($subscription.get()?.id as number, {
    delivery_window: deliveryWindow.join('-'),
  })
  await reloadSubscription()
}

export const setSupplement = async ({ supplement }: { supplement: string }) => {
  await updateSubscription($subscription.get()?.id as number, { supplement })
  await reloadSubscription()
}

export const setAddon = async ({ addonId }: { addonId: number }) => {
  await setSubscriptionAddon($subscription.get()?.id as number, addonId)
  await reloadSubscription()
}

export const removeAddon = async ({ addonId }: { addonId: number }) => {
  if (addonId) {
    await removeSubscriptionAddon($subscription.get()?.id as number, addonId)
  }

  await reloadSubscription()
}

export const updateAddons = async ({
  addons,
  selectedAddons,
}: {
  addons: Addon[]
  selectedAddons: { [sku: string]: number }
}) => {
  const promiseKey = 'updateAddons'
  const existingPromise = $promises.get()[promiseKey]

  if (existingPromise) {
    await existingPromise
  }

  const newPromise = async () => {
    const subscription = $subscription.get()
    if (!subscription) {
      throw new Error('Subscription not found')
    }

    const payload = addons.reduce((acc, addon) => {
      const selectedQuantity = selectedAddons[addon.shopifySku]
      const subscriptionAddon = subscription.addons.find(
        (subscriptionAddon) => subscriptionAddon.sku === addon.shopifySku,
      )

      if (subscriptionAddon) {
        if (selectedQuantity === subscriptionAddon.quantity) return acc

        acc.push({
          addon_product_id: addon.id,
          quantity: selectedQuantity,
          id: subscriptionAddon.id,
          _destroy: selectedQuantity === 0,
        })
      } else if (selectedQuantity) {
        acc.push({
          addon_product_id: addon.id,
          quantity: selectedQuantity,
        })
      }

      return acc
    }, [] as UpdateSubscriptionAddonsPayload)

    await updateSubscriptionAddons(subscription.id, payload)
  }

  try {
    setPromise({ promiseKey, promise: newPromise })
    await newPromise()
    await reloadSubscription()
    setPromise({ promiseKey, promise: undefined })
    return newPromise
  } catch (error) {
    setPromise({ promiseKey, promise: Promise.resolve() })
    throw error
  }
}

export const setShippingAddress = async ({
  shippingAddress,
}: {
  shippingAddress: Address
}) => {
  await updateSubscriptionShippingAddress(
    $subscription.get()?.id as number,
    shippingAddress,
  )
  await reloadSubscription()
}

export const setDeliveryNotes = async ({
  deliveryNotes,
}: {
  deliveryNotes: string
}) => {
  await updateSubscription($subscription.get()?.id as number, {
    delivery_notes: deliveryNotes,
  })
  await reloadSubscription()
}

export const triggerBillingInformationChangeEmail = async () => {
  await updateSubscriptionBillingAddress($subscription.get()?.id as number)
}

export const cancelSubscription = async () => {
  await cancelPopsSubscription($subscription.get()?.id as number)
  await reloadSubscription()
}

export const reactivateSubscription = async () => {
  await reactivatePopsSubscription($subscription.get()?.id as number)
  await reloadSubscription()
}

export const activateWeek = async ({ week }: { week: Dayjs }) => {
  await activateSubscriptionWeek(
    $subscription.get()?.id as number,
    week.format('YYYY-MM-DD'),
  )

  return Promise.all([
    loadSkippedWeeks(),
    loadSubscriptionWeek({ week, force: true }),
  ])
}

export const skipWeek = async ({ week }: { week: Dayjs }) => {
  const { campaignWeek } = $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]
  let promise = Promise.resolve()

  if (campaignWeek) {
    promise = campaignWeek.campaign.onWeekSkip({ campaignWeek })
  }

  return promise
    .then(() =>
      skipSubscriptionWeek(
        $subscription.get()?.id as number,
        week.startOf('week').format('YYYY-MM-DD'),
      ),
    )
    .catch(() => {})
    .finally(() =>
      Promise.all([
        loadSkippedWeeks(),
        loadSubscriptionWeek({ week, force: true }),
      ]),
    )
}

export const setWeekSku = async ({
  week,
  sku,
}: {
  week: Dayjs
  sku: string
}) => {
  const { campaignWeek } = $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]
  let promise = Promise.resolve()

  if (campaignWeek) {
    promise = campaignWeek.campaign.onWeekSkuChange({ campaignWeek, sku })
  }

  return promise
    .then(() =>
      updateSubscriptionWeek(
        $subscription.get()?.id as number,
        week.startOf('week').format('YYYY-MM-DD'),
        {
          sku,
        },
      ),
    )
    .catch(() => {})
    .finally(async () => loadSubscriptionWeek({ week, force: true }))
}

export const setWeekMeal = async ({
  week,
  meal,
  isAdding,
}: {
  week: Dayjs
  meal: string
  isAdding: boolean
}) => {
  if (isAdding) {
    await addWeekMeal({ week, meal })
  } else {
    await removeWeekMeal({ week, meal })
  }
}

export const addWeekMeal = async ({
  week,
  meal,
}: {
  week: Dayjs
  meal: string
}) => {
  // Load week into the store in case it hasn't been loaded
  await loadSubscriptionWeek({ week })

  const subscriptionWeek = $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]
  const { meals } = subscriptionWeek

  if (meals.includes(meal)) return Promise.resolve()

  meals.push(meal)

  const sku = buildSku(subscriptionWeek.daysOfFood, meals)

  return setWeekSku({ week, sku })
}

export const removeWeekMeal = async ({
  week,
  meal,
}: {
  week: Dayjs
  meal: string
}) => {
  // Load week into the store in case it hasn't been loaded
  await loadSubscriptionWeek({ week })

  const subscriptionWeek = $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]
  const meals = subscriptionWeek.meals.filter((m) => m !== meal)

  const sku = buildSku(subscriptionWeek.daysOfFood, meals)

  return setWeekSku({ week, sku })
}

export const setWeekDaysOfFood = async ({
  week,
  daysOfFood,
}: {
  week: Dayjs
  daysOfFood: number
}) => {
  // Load week into the store in case it hasn't been loaded
  await loadSubscriptionWeek({ week })

  const subscriptionWeek = $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]
  const sku = buildSku(daysOfFood, subscriptionWeek.meals)

  await setWeekSku({ week, sku })

  // Some SKU changes will cause meal swaps to be invalid and therefore be deleted. This
  // is why we must reload the meal swaps for the week
  return loadWeekMealSwaps({ week, force: true })
}

// async setWeekAddon({ getters, dispatch }, { week, addonId }) {
//   // Load week into the store in case it hasn't been loaded
//   await dispatch('loadSubscriptionWeek', { week });

//   if (addonId) {
//     await setWeekAddon(getters.subscription.id, week, addonId);
//   }

//   await dispatch('loadSubscriptionWeek', { week, force: true });
// },

export const setWeekSupplement = async ({
  week,
  supplement,
}: {
  week: Dayjs
  supplement: string
}) => {
  await updateSubscriptionWeek(
    $subscription.get()?.id as number,
    week.startOf('week').format('YYYY-MM-DD'),
    {
      supplement,
    },
  )
  await loadSubscriptionWeek({ week, force: true })
}

export const setWeekAddon = async ({
  week,
  addonId,
}: {
  week: Dayjs
  addonId: number
}) => {
  if (addonId) {
    await setSubscriptionWeekAddon(
      $subscription.get()?.id as number,
      week.startOf('week').format('YYYY-MM-DD'),
      addonId,
    )
  }

  await loadSubscriptionWeek({ week, force: true })
}

export const removeWeekAddon = async ({
  week,
  addonId,
}: {
  week: Dayjs
  addonId: number
}) => {
  if (addonId) {
    await removeSubscriptionWeekAddon(
      $subscription.get()?.id as number,
      week.startOf('week').format('YYYY-MM-DD'),
      addonId,
    )
  }

  await loadSubscriptionWeek({ week, force: true })
}

export const updateWeekAddons = async ({
  week,
  addons,
  selectedAddons,
}: {
  week: Dayjs
  addons: Addon[]
  selectedAddons: { [sku: string]: number }
}) => {
  const promiseKey = `updateWeekAddons-${week.format('YYYY-MM-DD')}`
  const existingPromise = $promises.get()[promiseKey]

  if (existingPromise) {
    await existingPromise
  }

  const newPromise = async () => {
    const subscriptionWeek = $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]
    const subscription = $subscription.get()

    if (!subscriptionWeek) {
      throw new Error('Subscription week not found')
    }
    if (!subscription) {
      throw new Error('Subscription not found')
    }

    const hasWeeklyPreference =
      subscriptionWeek.createdAt !== subscription.createdAt

    const payload = addons.reduce((acc, addon) => {
      const selectedQuantity = selectedAddons[addon.shopifySku]
      const subscriptionWeekAddon = subscriptionWeek.addons.find(
        (subscriptionWeekAddon) =>
          subscriptionWeekAddon.sku === addon.shopifySku,
      )

      if (subscriptionWeekAddon && (hasWeeklyPreference || existingPromise)) {
        if (selectedQuantity === subscriptionWeekAddon.quantity) return acc

        acc.push({
          addon_product_id: addon.id,
          quantity: selectedQuantity,
          id: subscriptionWeekAddon.id,
          _destroy: selectedQuantity === 0,
        })
      } else if (selectedQuantity) {
        acc.push({
          addon_product_id: addon.id,
          quantity: selectedQuantity,
        })
      }

      return acc
    }, [] as UpdateSubscriptionAddonsPayload)

    await updateSubscriptionWeekAddons(
      subscriptionWeek.id,
      week.format('YYYY-MM-DD'),
      payload,
      !hasWeeklyPreference,
    )
  }

  try {
    setPromise({ promiseKey, promise: newPromise })
    await newPromise()
    await loadSubscriptionWeek({ week, force: true })
    setPromise({ promiseKey, promise: undefined })
    return newPromise
  } catch (error) {
    setPromise({ promiseKey, promise: Promise.resolve() })
    throw error
  }
}

export const setWeekDeliveryWindow = async ({
  week,
  deliveryWindow,
}: {
  week: Dayjs
  deliveryWindow: string[]
}) => {
  await updateSubscriptionWeek(
    $subscription.get()?.id as number,
    week.startOf('week').format('YYYY-MM-DD'),
    {
      delivery_window: deliveryWindow.join('-'),
    },
  )
  await loadSubscriptionWeek({ week, force: true })
}

export const setWeekShippingAddress = async ({
  week,
  shippingAddress,
}: {
  week: Dayjs
  shippingAddress: Address
}) => {
  await updateSubscriptionWeekShippingAddress(
    $subscription.get()?.id as number,
    week.startOf('week').format('YYYY-MM-DD'),
    shippingAddress,
  )
  await loadSubscriptionWeek({ week, force: true })
}

export const setWeekDeliveryNotes = async ({
  week,
  deliveryNotes,
}: {
  week: Dayjs
  deliveryNotes: string
}) => {
  await updateSubscriptionWeek(
    $subscription.get()?.id as number,
    week.startOf('week').format('YYYY-MM-DD'),
    {
      delivery_notes: deliveryNotes,
    },
  )
  await loadSubscriptionWeek({ week, force: true })
}

export const setWeekMealSwap = async ({
  week,
  fromMeal,
  toMeal,
}: {
  week: Dayjs
  fromMeal: Meal
  toMeal: Meal
}) => {
  const subscriptionWeek = $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]
  const mealSwapToRemove = subscriptionWeek.getMealSwapByMealCode(
    fromMeal.mealCode,
  )

  // If a meal swap already exists for the 'fromMeal', remove it first
  if (mealSwapToRemove) {
    await removeSubscriptionMealSwap(
      $subscription.get()?.id as number,
      mealSwapToRemove.id as number,
    )
  }

  if (fromMeal !== toMeal) {
    const mealSwap = MealSwap.createMealSwapWithMeals(fromMeal, toMeal)

    await createSubscriptionWeekMealSwap(
      $subscription.get()?.id as number,
      week.startOf('week').format('YYYY-MM-DD'),
      {
        swap_from_day: mealSwap.swapFromDay,
        swap_from_meal: mealSwap.swapFromMeal,
        swap_to_day: mealSwap.swapToDay,
        swap_to_meal: mealSwap.swapToMeal,
        classic_meal: mealSwap.classicMeal,
      },
    )
  }

  return Promise.all([
    loadSubscriptionWeek({ week, force: true }),
    loadWeekMealSwaps({ week, force: true }),
  ])
}

export const clearWeekSakaraClassicMealSwaps = async ({
  week,
}: {
  week: Dayjs
}) => {
  const subscriptionWeek = $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]

  await Promise.all(
    subscriptionWeek.sakaraClassicMealSwaps.map((mealSwap) =>
      removeSubscriptionMealSwap(
        $subscription.get()?.id as number,
        mealSwap.id as number,
      ),
    ),
  )

  return Promise.all([
    loadSubscriptionWeek({ week, force: true }),
    loadWeekMealSwaps({ week, force: true }),
  ])
}

export const optInToCampaign = async ({
  startWeek,
  campaign,
  isDualOptIn,
}: {
  startWeek: Dayjs
  campaign: Campaign
  isDualOptIn: boolean
}) => {
  await campaign.optIn({ startWeek, isDualOptIn })

  const overlappedWeeks = campaign.getOverlappedWeeks(startWeek)
  const weeksToReload = $getAffectedWeeks(overlappedWeeks).get()

  return Promise.all([
    loadCampaignWeeks(),
    loadSubscriptionWeeks({ weeks: weeksToReload, force: true }),
  ])
}
export const optOutOfCampaign = async ({
  campaign,
  week,
}: {
  campaign: Campaign
  week: Dayjs
}) => {
  const { campaignWeek } = $subscriptionWeeks.get()[week.format('YYYY-MM-DD')]

  await campaign.optOut({ campaignWeek })

  await loadCampaignWeeks()
  await loadSubscriptionWeeks({
    weeks: campaign.getOverlappedWeeks(campaignWeek.campaignStartWeek),
    force: true,
  })
}

export const optInToDiscountOption = async ({
  discountOptionId,
  startWeek,
}: {
  discountOptionId: number
  startWeek: Dayjs
}) => {
  await optInToPopsDiscountOption(
    $subscription.get()?.id as number,
    discountOptionId,
    startWeek.format('YYYY-MM-DD'),
  )

  await loadDiscountOption({ discountOptionId })
  const discountOption = $discountOptions.get()[discountOptionId]

  return Promise.all([
    loadSubscriptionWeeks({ weeks: discountOption.omdStartDates, force: true }),
    loadDiscountOption({ discountOptionId, force: true }),
    loadDiscountOptionWeeks({
      subscriptionId: $subscription.get()?.id as number,
      force: true,
    }),
  ])
}

export const getSubscriptionStartDates = async ({
  zipCode,
  dayCount,
  meals,
}: {
  zipCode: string
  dayCount: number
  meals: string[]
}): Promise<Dayjs[]> => {
  const mealsPerDay = buildMealIdForSku(meals)
  const promiseKey = `getSubscriptionStartDates${zipCode}${dayCount}${mealsPerDay}`

  if ($promises.get()[promiseKey]) {
    return $promises.get()[promiseKey]
  }

  const promise = (async () => {
    const subscriptionStartDates = await fetchSubscriptionStartDates({
      zipCode,
      dayCount,
      mealsPerDay,
      weekCount: NEW_PROGRAM_WEEK_CAP - 1,
    })

    return subscriptionStartDates.map((week) => dayjs(week))
  })()

  $promises.setKey(promiseKey, promise)

  return promise
}

export const reset = async () => {
  clearCampaignWeeks()
  clearSubscriptionWeeksMenus()
  clearMealSwaps()
  clearCampaignWeeks()
  clearSkippedWeeks()
  clearPromises()
}
